import React, { ReactText } from "react";
import {
  TextField,
  IconButton,
  makeStyles,
  createStyles,
  Theme,
  TextFieldProps,
} from "@material-ui/core";
import NumberFormat, { NumberFormatProps } from "react-number-format";
import IncreaseIcon from "@material-ui/icons/ExpandLess";
import DecreaseIcon from "@material-ui/icons/ExpandMore";
import clsx from "clsx";
import { isNumberValid } from "../../helpers/utility";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      "position": "relative",

      "&:hover": {
        "& *": {
          display: "flex",
        },
      },

      "& .MuiOutlinedInput-input": {
        padding: theme.spacing(1.5),
      },

      "& .MuiOutlinedInput-adornedEnd": {
        paddingRight: theme.spacing(4),
      },

      // adornment inside spans
      "& div > span": {
        fontSize: "0.825rem",
        color: theme.palette.text.disabled,
      },
    },

    baseIcon: {
      position: "absolute",
      padding: 0,
      right: 0,
      display: "none",
      color: theme.palette.primary.main,
    },

    increaseIcon: {
      top: 0,
    },

    decreaseIcon: {
      bottom: 0,
    },
  })
);

type NumberFormatCustomProps = {
  value: number;
  name: string;
  step?: number;
  min?: number;
  max?: number;
  inputRef: (instance: NumberFormat | null) => void;
  onChange: (event: { target: { value: string; name: string } }) => void;
} & NumberFormatProps;

type operationTypes = "+" | "-";

function NumberFormatCustom(props: NumberFormatCustomProps) {
  const classes = useStyles();
  const { inputRef, onChange, value, name, min, max, decimalScale, ...other } = props;
  let { step } = props;

  step = isNumberValid(step) ? Number(step) : 1;

  const handleChangeValue = (type: operationTypes) => {
    let ret = value;

    if (type === "+") {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      ret = Number(ret) + step!;
    }

    if (type === "-") {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      ret = Number(ret) - step!;
    }

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    if (isNumberValid(min) && ret <= min!) {
      return min?.toString() || "";
    }

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    if (isNumberValid(max) && ret >= max!) {
      return max?.toString() || "";
    }

    return ret.toString();
  };

  const handleClick = (type: operationTypes) => {
    return () => {
      onChange({
        target: {
          value: handleChangeValue(type),
          name: name,
        },
      });
    };
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowUp") {
      onChange({
        target: {
          value: handleChangeValue("+"),
          name: name,
        },
      });
      return event.preventDefault();
    }

    if (event.key === "ArrowDown") {
      onChange({
        target: {
          value: handleChangeValue("-"),
          name: name,
        },
      });
      return event.preventDefault();
    }
  };

  return (
    <>
      <NumberFormat
        {...other}
        value={value === 0 ? "0" : value}
        getInputRef={inputRef}
        onValueChange={(values) => {
          onChange({
            target: {
              value: values.value,
              name: name,
            },
          });
        }}
        decimalScale={step === 0.01 && !decimalScale ? 2 : decimalScale}
        isNumericString
        decimalSeparator=","
        onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => handleKeyDown(e)}
      />

      <IconButton
        className={clsx(classes.baseIcon, classes.increaseIcon)}
        onClick={handleClick("+")}
      >
        <IncreaseIcon />
      </IconButton>

      <IconButton
        className={clsx(classes.baseIcon, classes.decreaseIcon)}
        onClick={handleClick("-")}
      >
        <DecreaseIcon />
      </IconButton>
    </>
  );
}

type TextFieldNumericProps = {
  value?: number | ReactText;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  step?: number;
  min?: number;
  max?: number;
  endAdornment?: React.ReactNode;
} & TextFieldProps;

function TextFieldNumeric(props: TextFieldNumericProps) {
  const classes = useStyles();
  const {
    className,
    value,
    disabled,
    error,
    helperText,
    onChange,
    min,
    max,
    step,
    endAdornment,
    ...others
  } = props;

  return (
    <TextField
      {...others}
      className={clsx(className, !disabled && classes.root)}
      value={value}
      disabled={disabled}
      error={error}
      helperText={helperText}
      onChange={onChange}
      variant="outlined"
      placeholder="Digite aqui"
      InputProps={{
        endAdornment: endAdornment,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        inputComponent: NumberFormatCustom as any,
        inputProps: { min: min, max: max, step: step },
      }}
    />
  );
}

export default TextFieldNumeric;
